import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/** Stable 4-digit tag from a server uid (disambiguates non-unique names). */
export function serverHash(serverId: string): string {
	let h = 2166136261
	for (let i = 0; i < serverId.length; i++) {
		h ^= serverId.charCodeAt(i)
		h = Math.imul(h, 16777619)
	}
	return String(Math.abs(h) % 10000).padStart(4, '0')
}

/** Display label: `My Server #4821`. */
export function formatServerName(
	name: string | null | undefined,
	serverId: string,
	fallback = 'Untitled server',
): string {
	const base = name?.trim() || fallback
	return `${base} #${serverHash(serverId)}`
}

/** How many lines of `el`'s typography fit in `availablePx` (for dynamic clamp + ellipsis). */
export function linesThatFit(el: HTMLElement, availablePx?: number): number {
	const cs = getComputedStyle(el)
	let lh = parseFloat(cs.lineHeight)
	if (!Number.isFinite(lh) || lh <= 0) {
		const fs = parseFloat(cs.fontSize) || 14
		lh = fs * 1.25
	}
	const h = availablePx ?? el.clientHeight
	if (h <= 0) return 0
	// Subpixel slack so we don't leave an almost-empty trailing line.
	return Math.max(1, Math.floor((h + 0.5) / lh))
}

/**
 * Leftover main-axis space in a column flex parent for `el`
 * (avoids re-measuring after line-clamp shrinks clientHeight).
 */
export function flexLeftoverHeight(el: HTMLElement): number {
	const parent = el.parentElement
	if (!parent) return el.clientHeight
	let used = 0
	for (const child of parent.children) {
		if (child === el) continue
		used += (child as HTMLElement).offsetHeight
	}
	const cs = getComputedStyle(el)
	const mt = parseFloat(cs.marginTop) || 0
	const mb = parseFloat(cs.marginBottom) || 0
	return Math.max(0, parent.clientHeight - used - mt - mb)
}

/** Inline styles for multiline clamp with ellipsis. Empty when `lines` is 0. */
export function lineClampStyle(lines: number): string {
	if (lines <= 0) return ''
	return `display:-webkit-box;-webkit-box-orient:vertical;overflow:hidden;-webkit-line-clamp:${lines};line-clamp:${lines}`
}

/** localStorage key for preferred file variant of a volume/chapter. */
export function bookVariantPrefKey(
	seriesId: string,
	type: string,
	num: string | number,
	subNum: string | number = 0,
): string {
	return `nuread:book-variant:${seriesId}:${type}:${num}.${subNum ?? 0}`
}

/** Human-readable label for a book file variant (basename without extension). */
export function bookVariantLabel(book: {
	path?: string
	localTitle?: string
	id?: string
}): string {
	const base =
		book.path?.split(/[/\\]/).pop() ||
		book.localTitle ||
		book.id ||
		'Variant'
	return base.replace(/\.(epub|ebook|cbz|cbr|zip|rar)$/i, '')
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChild<T> = T extends { child?: any } ? Omit<T, "child"> : T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithoutChildren<T> = T extends { children?: any } ? Omit<T, "children"> : T;
export type WithoutChildrenOrChild<T> = WithoutChildren<WithoutChild<T>>;
export type WithElementRef<T, U extends HTMLElement = HTMLElement> = T & { ref?: U | null };
